import React from 'react';

import BuildIcon from '@mui/icons-material/Build';
import PuzzleIcon from '@mui/icons-material/Extension';
import HomeIcon from '@mui/icons-material/Home';
import MailIcon from '@mui/icons-material/Mail';
import PeopleIcon from '@mui/icons-material/People';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import AccessibilityOptions from '../components/AccessibilityOptions';
import { caseStudies, caseStudyPath } from '../data/CaseStudies';
import { insightPath, insights } from '../data/Insights';
import AboutUs from '../pages/AboutUs';
import CaseStudies from '../pages/CaseStudies';
import CaseStudyPage from '../pages/CaseStudyPage';
import CmsGovernance from '../pages/CmsGovernance';
import ContactUs from '../pages/ContactUs';
import Faq from '../pages/Faq';
import Home from '../pages/Home';
import InsightArticlePage from '../pages/InsightArticlePage';
import Insights from '../pages/Insights';
import PrivacyNotice from '../pages/PrivacyNotice';
import Services from '../pages/Services';
import Solutions from '../pages/Solutions';

type AppRoute = {
  label1: string;
  label2: string;
  path: string;
  topLabel: string;
  component: React.ComponentType;
  sidebar: boolean;
  footer: boolean;
  // Show the shared footer on the page but keep the route out of the footer link list.
  listInFooter?: boolean;
  icon: typeof HomeIcon;
};

// One route per study so each has its own URL, metadata, and sitemap entry.
const caseStudyRoutes: AppRoute[] = caseStudies.map((study) => ({
  label1: 'CMS',
  label2: 'Case Study',
  path: caseStudyPath(study.id),
  topLabel: study.title,
  component: () => <CaseStudyPage study={study} />,
  sidebar: false,
  footer: true,
  listInFooter: false,
  icon: PuzzleIcon,
}));

const insightRoutes: AppRoute[] = insights.map((article) => ({
  label1: 'Practical',
  label2: 'Insight',
  path: insightPath(article.id),
  topLabel: article.title,
  component: () => <InsightArticlePage article={article} />,
  sidebar: false,
  footer: true,
  listInFooter: false,
  icon: PuzzleIcon,
}));

const routes: AppRoute[] = [
  {
    label1: 'Home',
    label2: 'Page',
    path: '/',
    topLabel: 'Home',
    component: Home,
    sidebar: true,
    footer: true,
    icon: HomeIcon,
  },
  {
    label1: 'About',
    label2: 'Us',
    path: '/about-us',
    topLabel: 'About',
    component: AboutUs,
    sidebar: true,
    footer: true,
    icon: PeopleIcon,
  },
  {
    label1: 'CMS',
    label2: 'Case Studies',
    path: '/case-studies',
    topLabel: 'Case Studies',
    component: CaseStudies,
    sidebar: true,
    footer: true,
    icon: PuzzleIcon,
  },
  {
    label1: 'Our',
    label2: 'Services',
    path: '/services',
    topLabel: 'Services',
    component: Services,
    sidebar: true,
    footer: true,
    icon: BuildIcon,
  },
  {
    label1: 'Contact',
    label2: 'Us',
    path: '/contact-us',
    topLabel: 'Contact Us',
    component: ContactUs,
    sidebar: true,
    footer: true,
    icon: MailIcon,
  },
  {
    label1: 'Selected',
    label2: 'Work',
    path: '/solutions',
    topLabel: 'Selected Work',
    component: Solutions,
    sidebar: false,
    footer: true,
    icon: PuzzleIcon,
  },
  {
    label1: 'CMS',
    label2: 'Governance',
    path: '/cms-governance',
    topLabel: 'CMS Governance',
    component: CmsGovernance,
    sidebar: false,
    footer: true,
    icon: PuzzleIcon,
  },
  {
    label1: 'Practical',
    label2: 'Insights',
    path: '/insights',
    topLabel: 'Insights',
    component: Insights,
    sidebar: false,
    footer: true,
    icon: PuzzleIcon,
  },
  {
    label1: 'Common',
    label2: 'Questions',
    path: '/faq',
    topLabel: 'FAQ',
    component: Faq,
    sidebar: false,
    footer: true,
    icon: PuzzleIcon,
  },
  {
    label1: 'Privacy',
    label2: 'Notice',
    path: '/privacy',
    topLabel: 'Privacy Notice',
    component: PrivacyNotice,
    sidebar: false,
    footer: true,
    icon: HomeIcon,
  },
  ...caseStudyRoutes,
  ...insightRoutes,
  {
    label1: '',
    label2: '',
    path: '*',
    topLabel: '',
    component: NoMatch,
    sidebar: false,
    footer: false,
    icon: HomeIcon,
  },
];

function NoMatch() {
  return (
    <>
      <Box sx={{ maxWidth: 800, mx: 'auto', px: 3, py: 8 }}>
        <Typography
          variant='h3'
          component='h1'
        >
          Page not found
        </Typography>
        <Typography sx={{ mt: 2 }}>
          <Link to='/'>Return to the home page</Link>
        </Typography>
        <AccessibilityOptions />
      </Box>
    </>
  );
}

export default routes;
